import { useEffect, useState } from "react";
import { AppShell } from "../app/AppShell";
import { useMe } from "../app/AuthGate";
import { Button } from "../components/Button";
import { Alert, Card, EmptyState, Loading } from "../components/Card";
import { NewOrgForm, OrgCard, OrgGrid, OrgManage, RoleGuide } from "../components/Orgs";
import { PageHead } from "../components/Page";
import { authErrText } from "../lib/auth";
import { gitConnApi } from "../lib/gitconn";
import type { GitConn } from "../lib/gitconn";
import { orgApi } from "../lib/orgs";
import type { Org, OrgMember, OrgRole } from "../lib/orgs";
import { projectApi } from "../lib/project";
import type { Project } from "../lib/project";
import { tx } from "../i18n";

/* ══ 관리 › 조직(#/orgs) — 시안 docs/screens/pr-port.md §25 · 결정 docs/decisions/org.md ══
   로그인한 누구나 연다 — 관리자는 전부, 그 밖의 사람은 자기가 멤버인 조직만 보인다(서버가 고른다).
   열 때 한 번 읽고, 쓴 뒤에 다시 읽는다. git 연결 · 프로젝트 목록은 관리자에게만 읽는다(`/admin/` 문이다). */

export function OrgsPage() {
  const me = useMe()?.me;
  const isAdmin = me?.role === "admin";
  const [orgs, setOrgs] = useState<Org[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [members, setMembers] = useState<OrgMember[] | null>(null);
  const [projects, setProjects] = useState<Project[] | undefined>(undefined);
  const [conns, setConns] = useState<GitConn[] | undefined>(undefined);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function load() {
    orgApi
      .list()
      .then((o) => {
        setOrgs(o);
        setLoadErr(null);
      })
      .catch((e) => setLoadErr(authErrText(e)));
    if (isAdmin) {
      // 못 읽으면 고르기 대신 읽기 글자로 선다 — 조직 목록은 막지 않는다.
      projectApi.list().then(setProjects).catch(() => setProjects(undefined));
      gitConnApi.list().then((r) => setConns(r.connections)).catch(() => setConns(undefined));
    }
  }
  useEffect(load, [isAdmin]);

  useEffect(() => {
    setMembers(null);
    if (!picked) return;
    orgApi
      .members(picked)
      .then(setMembers)
      .catch((e) => {
        setMembers([]);
        setErr(authErrText(e));
      });
  }, [picked]);

  async function run(f: () => Promise<unknown>) {
    setBusy(true);
    setErr(null);
    try {
      await f();
      load();
    } catch (e) {
      setErr(authErrText(e));
    } finally {
      setBusy(false);
    }
  }

  const pickedOrg = orgs?.find((o) => o.id === picked) ?? null;
  const orgName = (id: string) => orgs?.find((o) => o.id === id)?.name ?? id;

  return (
    <AppShell route="/orgs">
      <PageHead
        title={tx("nav.orgs")}
        lead={tx("orgs.lead")}
        meta={orgs ? <span className="caption">{tx("orgs.summary", { n: orgs.length })}</span> : undefined}
        actions={
          <>
            <Button onClick={load}>{tx("common.action.refresh")}</Button>
            {isAdmin && !adding ? (
              <Button variant="primary" onClick={() => setAdding(true)}>
                {tx("orgs.add")}
              </Button>
            ) : null}
          </>
        }
      />
      {loadErr ? <Alert tone="error">{tx("orgs.loadFailed", { err: loadErr })}</Alert> : null}
      <Card title={tx("orgs.guide.title")}>
        <RoleGuide />
      </Card>
      {adding ? (
        <NewOrgForm
          busy={busy}
          error={err}
          onCancel={() => {
            setAdding(false);
            setErr(null);
          }}
          onSubmit={(name) =>
            void run(async () => {
              const o = await orgApi.create(name);
              setAdding(false);
              setPicked(o.id);
            })
          }
        />
      ) : null}
      {!orgs && !loadErr ? <Loading text={tx("orgs.loading")} /> : null}
      {orgs && orgs.length === 0 ? <EmptyState icon="users" title={tx("orgs.empty.title")} desc={tx(isAdmin ? "orgs.empty.adminDesc" : "orgs.empty.desc")} /> : null}
      {orgs && orgs.length ? (
        <OrgGrid>
          {orgs.map((o) => (
            <OrgCard
              key={o.id}
              o={o}
              isAdmin={isAdmin}
              picked={picked === o.id}
              onPick={() => {
                setErr(null);
                setPicked(picked === o.id ? null : o.id);
              }}
            />
          ))}
        </OrgGrid>
      ) : null}
      {pickedOrg ? (
        <OrgManage
          o={pickedOrg}
          members={members}
          isAdmin={isAdmin}
          projects={projects}
          conns={conns}
          busy={busy}
          error={adding ? null : err}
          orgName={orgName}
          onRename={(name) => void run(() => orgApi.update(pickedOrg.id, { name }))}
          onGit={(id) => void run(() => orgApi.update(pickedOrg.id, { gitConnectionId: id }))}
          onProjects={(ids) => void run(() => orgApi.setProjects(pickedOrg.id, ids))}
          onSetMember={(u: string, r: OrgRole) => void run(async () => setMembers(await orgApi.setMember(pickedOrg.id, u, r)))}
          onRemoveMember={(m) => void run(async () => setMembers(await orgApi.removeMember(pickedOrg.id, m.userId)))}
          onDelete={() =>
            void run(async () => {
              await orgApi.remove(pickedOrg.id);
              setPicked(null);
            })
          }
          onClose={() => setPicked(null)}
        />
      ) : null}
    </AppShell>
  );
}
